import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  BarChart2, CheckSquare, ChevronLeft, FileJson, FolderOpen,
  Layers, Plus, Trash2,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useProject, useUpdateProject, useProjectThroughput } from '../../api/projects';
import { useTaskList, useBulkCreateTasks } from '../../api/tasks';
import { useEngineerList } from '../../api/engineers';
import { useEpicsByProject, useCreateEpic, useDeleteEpic } from '../../api/epics';
import type { BulkCreateTaskItem } from '../../api/tasks';
import type { EpicDto, TaskStatus } from '../../types/api';
import { applyServerErrors } from '../../lib/formErrors';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { DetailPageSkeleton, Skeleton } from '@/components/ui/skeleton';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import ErrorState from '../../components/ui/ErrorState';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { formatDate, daysUntil } from '../../lib/dates';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

const STATUS_VARIANT: Record<TaskStatus, 'green' | 'red' | 'gray'> = {
  active: 'green', blocked: 'red', done: 'gray',
};

const EPIC_STATUS_STYLES: Record<string, string> = {
  NotStarted: 'bg-muted text-muted-foreground',
  InProgress:  'bg-primary/10 text-primary',
  Done:        'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
};

const EPIC_STATUS_LABEL: Record<string, string> = {
  NotStarted: 'Not started', InProgress: 'In progress', Done: 'Done',
};

const CHART_OPTIONS = {
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { display: false }, title: { display: false } },
  scales: { x: { grid: { display: false } }, y: { beginAtZero: true, ticks: { precision: 0 } } },
};

type Tab = 'epics' | 'tasks' | 'throughput';
interface EditForm { name: string; description: string; }
interface EpicForm { title: string; description: string; }

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const isPm       = can('project_manager');
  const isProductPm = can('product_manager');

  const [tab, setTab]           = useState<Tab>('epics');
  const [editing, setEditing]   = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [importError, setImportError] = useState<string | null>(null);
  const [showEpicForm, setShowEpicForm] = useState(false);
  const { confirm, dialog } = useConfirm();

  const { data: project, isLoading, error, refetch } = useProject(id!);
  const { data: throughput, isLoading: throughputLoading } = useProjectThroughput(id!);
  const { data: tasks, isLoading: tasksLoading } = useTaskList({ projectId: id, limit: 50 });
  const { data: epics, isLoading: epicsLoading } = useEpicsByProject(id!);
  const { data: engineers } = useEngineerList();
  const updateProject  = useUpdateProject(id!);
  const bulkCreate     = useBulkCreateTasks();
  const createEpic     = useCreateEpic(id!);
  const deleteEpic     = useDeleteEpic(id!);

  const engineerMap = new Map(engineers?.map((e) => [e.id, e.name]) ?? []);

  const { register, handleSubmit, setError, reset, formState: { errors } } = useForm<EditForm>();
  const epicForm = useForm<EpicForm>();

  const onEdit = handleSubmit((values) => {
    updateProject.mutate(
      { name: values.name, description: values.description || undefined },
      {
        onSuccess: () => { toast.success('Project updated.'); setEditing(false); },
        onError:   (e) => applyServerErrors(e, setError),
      },
    );
  });

  const onArchive = async () => {
    if (!await confirm({ title: `Archive "${project?.name}"?`, description: 'This cannot be undone.', confirmLabel: 'Archive' })) return;
    updateProject.mutate(
      { archive: true },
      {
        onSuccess: () => { toast.success('Project archived.'); navigate('/projects'); },
        onError:   () => toast.error('Failed to archive project.'),
      },
    );
  };

  const onImport = () => {
    setImportError(null);
    let parsed: BulkCreateTaskItem[];
    try {
      parsed = JSON.parse(importJson);
      if (!Array.isArray(parsed)) throw new Error('Expected a JSON array.');
    } catch (e) {
      setImportError(e instanceof Error ? e.message : 'Invalid JSON.');
      return;
    }
    bulkCreate.mutate(
      { projectId: id!, tasks: parsed },
      {
        onSuccess: (result) => {
          toast.success(result.created === 1 ? '1 task imported.' : `${result.created} tasks imported.`);
          if (result.failed.length > 0) {
            toast.warning(`${result.failed.length} task(s) skipped.`);
            setImportError(result.failed.map((f) => `Row ${f.index + 1}: ${f.error}`).join('\n'));
          } else { setShowImport(false); setImportJson(''); }
        },
        onError: () => setImportError('Import failed. Please try again.'),
      },
    );
  };

  const onCreateEpic = epicForm.handleSubmit((values) => {
    createEpic.mutate(
      { title: values.title, description: values.description || undefined },
      {
        onSuccess: () => { toast.success('Epic created.'); epicForm.reset(); setShowEpicForm(false); },
        onError:   () => toast.error('Failed to create epic.'),
      },
    );
  });

  const onDeleteEpic = async (epic: EpicDto) => {
    if (!await confirm({ title: `Delete "${epic.title}"?`, description: 'Tasks in this epic will lose their epic association.', confirmLabel: 'Delete' })) return;
    deleteEpic.mutate(epic.id, { onSuccess: () => toast.success('Epic deleted.') });
  };

  if (isLoading) return <DetailPageSkeleton maxW="4xl" />;
  if (error || !project) return <ErrorState onRetry={refetch} />;

  const taskItems = tasks?.items ?? [];
  const chartLabels = (throughput ?? []).map((w) => {
    const d = new Date(w.weekOf);
    return `${d.toLocaleString('default', { month: 'short' })} ${d.getDate()}`;
  });
  const chartData = {
    labels: chartLabels,
    datasets: [{ data: (throughput ?? []).map((w) => w.pointsDelivered), backgroundColor: 'rgb(99, 102, 241)', borderRadius: 4 }],
  };

  return (
    <div className="max-w-3xl">
      {dialog}
      <Link to="/projects" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> All projects
      </Link>

      <PageHeader
        title={project.name}
        actions={
          isPm && project.isActive ? (
            <div className="flex gap-2">
              <Button size="sm" variant="ghost"
                onClick={() => { setEditing((v) => !v); reset({ name: project.name, description: project.description ?? '' }); }}>
                {editing ? 'Cancel' : 'Edit'}
              </Button>
              <Button size="sm" variant="ghost" onClick={onArchive} loading={updateProject.isPending}>Archive</Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowImport((v) => !v); setImportError(null); }}>
                {showImport ? 'Cancel' : 'Import tasks'}
              </Button>
              <Button size="sm" onClick={() => navigate(`/tasks/new?projectId=${id}`)}>New task</Button>
            </div>
          ) : undefined
        }
      />

      {/* Meta */}
      {!editing && (
        <Card className={cn('mb-6 overflow-hidden border-l-4', project.isActive ? 'border-l-primary' : 'border-l-gray-300')}>
          <CardContent className="p-5">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Badge label={project.isActive ? 'active' : 'archived'} variant={project.isActive ? 'green' : 'gray'} />
              <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                Created {formatDate(project.createdAt)}
              </span>
            </div>
            {project.description && <p className="text-sm text-muted-foreground">{project.description}</p>}
          </CardContent>
        </Card>
      )}

      {editing && (
        <Card className="mb-6 overflow-hidden">
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
              <FolderOpen className="h-4 w-4 text-primary" />
            </div>
            <p className="text-sm font-semibold text-foreground">Edit project</p>
          </div>
          <form onSubmit={onEdit}>
            <div className="space-y-4 px-5 py-5">
              <div className="space-y-1.5">
                <Label>Name <span className="text-destructive">*</span></Label>
                <Input {...register('name', { required: 'Name is required' })} />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea rows={2} {...register('description')} />
              </div>
            </div>
            <div className="flex gap-2 border-t border-border bg-muted/30 px-5 py-3">
              <Button size="sm" type="submit" loading={updateProject.isPending}>Save</Button>
              <Button size="sm" variant="ghost" type="button" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {showImport && (
        <Card className="mb-6 overflow-hidden">
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-50 dark:bg-violet-950/40">
              <FileJson className="h-4 w-4 text-violet-600 dark:text-violet-400" />
            </div>
            <p className="text-sm font-semibold text-foreground">Import tasks from JSON</p>
          </div>
          <CardContent className="p-5">
            <p className="mb-3 text-xs text-muted-foreground">
              Paste a JSON array. Each item must have <code className="rounded bg-muted px-1">title</code>, <code className="rounded bg-muted px-1">points</code>, and <code className="rounded bg-muted px-1">dueDate</code> (YYYY-MM-DD).
            </p>
            <Textarea rows={8} className="mb-3 font-mono text-xs" placeholder="Paste JSON here…"
              value={importJson} onChange={(e) => setImportJson(e.target.value)} />
            {importError && (
              <pre className="mb-3 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive whitespace-pre-wrap">{importError}</pre>
            )}
            <Button size="sm" onClick={onImport} loading={bulkCreate.isPending}>Import</Button>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="mb-5 flex border-b border-border">
        {(['epics', 'tasks', 'throughput'] as Tab[]).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={cn(
              'px-4 py-2 text-sm font-medium capitalize border-b-2 -mb-px transition-colors',
              tab === t ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground',
            )}>
            {t}
          </button>
        ))}
      </div>

      {/* Epics tab */}
      {tab === 'epics' && (
        <div className="space-y-4">
          {isProductPm && project.isActive && (
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setShowEpicForm((v) => !v)}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                {showEpicForm ? 'Cancel' : 'New epic'}
              </Button>
            </div>
          )}

          {showEpicForm && (
            <Card className="overflow-hidden">
              <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-50 dark:bg-violet-950/40">
                  <Layers className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                </div>
                <p className="text-sm font-semibold text-foreground">New epic</p>
              </div>
              <form onSubmit={onCreateEpic}>
                <div className="space-y-4 px-5 py-5">
                  <div className="space-y-1.5">
                    <Label>Title <span className="text-destructive">*</span></Label>
                    <Input {...epicForm.register('title', { required: 'Title is required' })} placeholder="e.g. User Authentication" />
                    {epicForm.formState.errors.title && (
                      <p className="text-xs text-destructive">{epicForm.formState.errors.title.message}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Description</Label>
                    <Textarea rows={2} {...epicForm.register('description')} placeholder="What does this epic cover?" />
                  </div>
                </div>
                <div className="flex gap-2 border-t border-border bg-muted/30 px-5 py-3">
                  <Button size="sm" type="submit" loading={createEpic.isPending}>Create epic</Button>
                  <Button size="sm" variant="ghost" type="button" onClick={() => setShowEpicForm(false)}>Cancel</Button>
                </div>
              </form>
            </Card>
          )}

          {epicsLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (epics?.length ?? 0) === 0 ? (
            <Card>
              <EmptyState icon={Layers} title="No epics yet"
                description="Epics group related tasks together. Create one to start building the backlog."
                action={isProductPm && project.isActive
                  ? <Button size="sm" onClick={() => setShowEpicForm(true)}>Create first epic</Button>
                  : undefined}
              />
            </Card>
          ) : (
            <Card className="divide-y divide-border overflow-hidden p-0">
              {epics!.map((epic) => (
                <div
                  key={epic.id}
                  className="flex cursor-pointer items-center gap-3 px-5 py-3 hover:bg-muted/40 transition-colors"
                  onClick={() => navigate(`/epics/${epic.id}`)}
                >
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-violet-50 dark:bg-violet-950/40">
                    <Layers className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">{epic.title}</p>
                    {epic.description && (
                      <p className="truncate text-xs text-muted-foreground">{epic.description}</p>
                    )}
                  </div>
                  <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-xs font-medium', EPIC_STATUS_STYLES[epic.status])}>
                    {EPIC_STATUS_LABEL[epic.status]}
                  </span>
                  {epic.sprintId && (
                    <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      In sprint
                    </span>
                  )}
                  {isProductPm && project.isActive && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onDeleteEpic(epic); }}
                      className="shrink-0 rounded p-1 text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </Card>
          )}
        </div>
      )}

      {/* Tasks tab */}
      {tab === 'tasks' && (
        <section>
          {tasksLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : taskItems.length === 0 ? (
            <Card>
              <EmptyState icon={CheckSquare} title="No tasks yet"
                description="Tasks assigned to this project will appear here."
                action={isPm && project.isActive
                  ? <Button size="sm" onClick={() => navigate(`/tasks/new?projectId=${id}`)}>Add first task</Button>
                  : undefined}
              />
            </Card>
          ) : (
            <Card className="divide-y divide-border overflow-hidden p-0">
              {taskItems.map((task) => {
                const days = daysUntil(task.dueDate);
                const assigneeName = task.assigneeId ? (engineerMap.get(task.assigneeId) ?? 'Unknown') : null;
                const assigneeInitials = assigneeName
                  ? assigneeName.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase()
                  : null;
                return (
                  <div key={task.id}
                    className="flex cursor-pointer items-center gap-3 px-5 py-3 transition-colors hover:bg-muted/40"
                    onClick={() => navigate(`/tasks/${task.id}`)}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground">{task.title}</p>
                      {assigneeName && (
                        <div className="mt-0.5 flex items-center gap-1.5">
                          <div className="flex h-4 w-4 items-center justify-center rounded-full bg-primary/10 text-[9px] font-bold text-primary">
                            {assigneeInitials}
                          </div>
                          <p className="text-xs text-muted-foreground">{assigneeName}</p>
                        </div>
                      )}
                    </div>
                    <span className="shrink-0 text-xs text-muted-foreground">{task.points} pts</span>
                    <span className={cn('shrink-0 text-xs', days < 0 ? 'font-medium text-destructive' : days <= 3 ? 'text-yellow-600' : 'text-muted-foreground')}>
                      {days < 0 ? `${Math.abs(days)}d overdue` : days === 0 ? 'due today' : `${days}d left`}
                    </span>
                    <Badge label={task.status} variant={STATUS_VARIANT[task.status]} />
                  </div>
                );
              })}
            </Card>
          )}
        </section>
      )}

      {/* Throughput tab */}
      {tab === 'throughput' && (
        <Card className="overflow-hidden">
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
              <BarChart2 className="h-4 w-4 text-primary" />
            </div>
            <p className="text-sm font-semibold text-foreground">Delivered points — last 6 weeks</p>
          </div>
          <CardContent className="p-5">
            {throughputLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : (throughput?.length ?? 0) === 0 ? (
              <p className="py-4 text-center text-xs text-muted-foreground">No throughput data yet.</p>
            ) : (
              <div className="h-44"><Bar data={chartData} options={CHART_OPTIONS} /></div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
